import { provideHttpClient } from '@angular/common/http';
import { DEFAULT_CURRENCY_CODE, NgModule } from '@angular/core';
import { initializeApp, provideFirebaseApp } from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { AngularFireModule } from '@angular/fire/compat';
import { REGION, USE_EMULATOR } from '@angular/fire/compat/functions';
import { getDatabase, provideDatabase } from '@angular/fire/database';
import { getFirestore, provideFirestore } from '@angular/fire/firestore';
import { connectFunctionsEmulator, getFunctions, provideFunctions } from '@angular/fire/functions';
import { getStorage, provideStorage } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import {
    DateAdapter,
    MAT_DATE_FORMATS,
    MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { provideRouter, RouterOutlet } from '@angular/router';
import { provideTranslateService, TranslateModule } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { CURRENCY_MASK_CONFIG } from 'ng2-currency-mask';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxSpinnerModule } from 'ngx-spinner';
import { ToastrModule } from 'ngx-toastr';
import { environment } from '../environments/environment';
import { AppComponent } from './app.component';
import {
    AUTH_EXPIRATION_TIME,
    FIREBASE_REGION,
    INACTIVE_PING_CHECK,
} from './app.constants';
import { routes } from './app.routes';
import { SystemWarningsModule } from './core/components/check-version/system-warnings.module';
import { SystemWarningsService } from './core/components/check-version/system-warnings.service';
import { InactiveUserModule } from './core/components/inactive-user/inactive-user.module';
import { CurrencyConfig } from './utils/currency-config';
import { APP_DATE_FORMATS, AppDateAdapter } from './utils/date-adapter';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        NgxSpinnerModule,
        NgxPermissionsModule.forRoot(),
        InactiveUserModule.forRoot({
            timeout: AUTH_EXPIRATION_TIME,
            ping: INACTIVE_PING_CHECK,
        }),
        SystemWarningsModule,
        NgCircleProgressModule.forRoot({
            radius: 22,
            showSubtitle: false,
            outerStrokeWidth: 5,
            innerStrokeWidth: 1,
            outerStrokeColor: '#78C000',
            innerStrokeColor: '#C7E596',
            animationDuration: 300,
            titleFontSize: '13',
            unitsFontSize: '13',
            titleFontWeight: '600',
            unitsFontWeight: '600',
            outerStrokeLinecap: 'butt',
            space: 2,
            titleColor: '#8C969F',
            unitsColor: '#8C969F',
        }),
        ToastrModule.forRoot({
            timeOut: 4000,
            positionClass: 'toast-bottom-right',
            preventDuplicates: true,
            iconClasses: {
                info: 'toast-info',
            },
        }),
        TranslateModule,
        AngularFireModule.initializeApp(environment.firebaseConfig),
        RouterOutlet,
        MatDialogModule,
        NgxDatatableModule.forRoot({
            messages: {
                emptyMessage: 'Keine Einträge vorhanden',
                selectedMessage: 'Auswählen',
                totalMessage: 'Gesamt',
            },
        }),
    ],
    bootstrap: [AppComponent],
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'de' },
        { provide: DateAdapter, useClass: AppDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
        { provide: REGION, useValue: FIREBASE_REGION },
        MAT_TOOLTIP_SCROLL_STRATEGY_FACTORY_PROVIDER,
        { provide: MAT_DIALOG_DATA, useValue: {} },
        { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },
        { provide: CURRENCY_MASK_CONFIG, useValue: CurrencyConfig },
        provideFirebaseApp(() => initializeApp(environment.firebaseConfig)),
        provideFirestore(() => getFirestore()),
        provideFunctions(() => {
            const functions = getFunctions();
            functions.region = FIREBASE_REGION;
            if (environment.env === 'emulator') {
                connectFunctionsEmulator(functions, 'localhost', 5001);
            }
            return functions;
        }),
        provideAuth(() => getAuth()),
        provideStorage(() => getStorage()),
        provideDatabase(() => getDatabase()),
        provideTranslateService({
            loader: provideTranslateHttpLoader({
                prefix: '/assets/i18n/',
                suffix: '.json',
            }),
            fallbackLang: 'de',
            lang: 'de',
        }),
        {
            provide: USE_EMULATOR,
            useValue:
                environment.env === 'emulator'
                    ? ['localhost', 9000]
                    : undefined,
        },
        provideHttpClient(),
        provideRouter(routes),
    ],
})
export class AppModule {
    constructor(private systemWarningsService: SystemWarningsService) {
        this.systemWarningsService.init();
    }
}
